import { format, subDays } from "date-fns"
import {
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  InfoIcon,
  RefreshCw,
  Search,
  WebhookIcon,
  X,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { useParams } from "react-router-dom"

import type { AppDateRange } from "@/components/commons"
import { AppDateRangePicker } from "@/components/commons"
import { CallbackPagination } from "@/components/commons/callback-pagination"
import { ClipboardButton } from "@/components/commons/clipboard-button"
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDescription,
  TimelineHeader,
  TimelineIcon,
  TimelineItem as VerticalTimelineItem,
  TimelineTitle,
} from "@/components/commons/timeline"
import {
  InfoItem,
  SectionCard,
} from "@/components/shared/detail-components"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import FRONTEND_ROUTES from "@/constants/front-end-routes"
import {
  useGetWebhookResourceById,
  useGetWebhookResources,
  useResendWebhookResources,
  useWebhookById,
} from "@/hooks/webhooks"
import { cn } from "@/lib/utils"
import { DateHelper } from "@/utils"

import {
  ALL_WEBHOOK_EVENTS,
  EVENT_LABELS,
  getEventLabel,
  STATUS_COLORS,
  STATUS_LABELS,
  type WebhookEvent,
} from "./events"
import { HttpViewer } from "./http-viewer"
import { WebhookDetailsSkeleton } from "./webhook-details-skeleton"
const API_DATE_FORMAT = "yyyy-MM-dd"

// Inbox abre nos últimos 30 dias: sem recorte, a consulta varre a tabela toda
// e o operador quase nunca procura evento mais velho que isso.
const RESOURCES_RANGE_IN_DAYS = 30

const DEFAULT_RESOURCES_RANGE: AppDateRange = {
  from: subDays(new Date(), RESOURCES_RANGE_IN_DAYS - 1),
  to: new Date(),
}

export const WebhookDetails = () => {
  const { t } = useTranslation()
  const params = useParams()
  const webhookId = params?.webhookId as string
  const [resourcesPageSize, setResourcesPageSize] =
    useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [resourceIdFilter, setResourceIdFilter] =
    useState("")
  const [statusFilter, setStatusFilter] = useState<
    WebhookResourceStatus | ""
  >("")
  const [resourceTypeFilter, setResourceTypeFilter] =
    useState("")
  const [dateRange, setDateRange] = useState<AppDateRange>(
    DEFAULT_RESOURCES_RANGE
  )
  const [resourceTypeOpen, setResourceTypeOpen] =
    useState(false)
  const [resourceTypeSearch, setResourceTypeSearch] =
    useState("")
  const [selectedResourceId, setSelectedResourceId] =
    useState<string | null>(null)
  const [showSecretKey, setShowSecretKey] = useState(false)
  const [expandedEventId, setExpandedEventId] = useState<
    string | null
  >(null)
  const { data, isFetching, error } =
    useWebhookById(webhookId)
  const {
    data: resourcesData,
    refetch: refetchResources,
    isFetching: isFetchingResources,
  } = useGetWebhookResources({
    webhookId,
    filters: {
      limit: resourcesPageSize,
      offset: currentPage,
      ...(resourceIdFilter && {
        resourceId: resourceIdFilter,
      }),
      ...(statusFilter && {
        status: statusFilter as WebhookResourceStatus,
      }),
      ...(resourceTypeFilter && {
        resourceType: resourceTypeFilter,
      }),
      startDate: format(dateRange.from, API_DATE_FORMAT),
      endDate: format(dateRange.to, API_DATE_FORMAT),
    },
  })
  const {
    data: resourceDetails,
    isLoading: isLoadingResource,
    refetch: refetchResourceDetails,
    isFetching: isFetchingResourceDetails,
  } = useGetWebhookResourceById(
    webhookId,
    selectedResourceId || ""
  )
  const resendMutation = useResendWebhookResources()
  const hasData = Boolean(data)
  const resources: WebhookResource[] =
    resourcesData?.resources || []
  const isCustomRange =
    format(dateRange.from, API_DATE_FORMAT) !==
      format(
        DEFAULT_RESOURCES_RANGE.from,
        API_DATE_FORMAT
      ) ||
    format(dateRange.to, API_DATE_FORMAT) !==
      format(DEFAULT_RESOURCES_RANGE.to, API_DATE_FORMAT)
  const hasActiveFilters =
    resourceIdFilter ||
    statusFilter ||
    resourceTypeFilter ||
    isCustomRange
  const filteredEvents = resourceTypeSearch
    ? ALL_WEBHOOK_EVENTS.filter(
        (event) =>
          EVENT_LABELS[event]
            .toLowerCase()
            .includes(resourceTypeSearch.toLowerCase()) ||
          event
            .toLowerCase()
            .includes(resourceTypeSearch.toLowerCase())
      )
    : ALL_WEBHOOK_EVENTS
  const clearFilters = () => {
    setResourceIdFilter("")
    setStatusFilter("")
    setResourceTypeFilter("")
    setDateRange(DEFAULT_RESOURCES_RANGE)
    setCurrentPage(1)
  }
  const handleResend = async (
    webhookResourceId: string
  ) => {
    if (!webhookId) return
    resendMutation.mutate({
      webhookConfId: webhookId,
      webhookId: webhookResourceId,
    })
  }
  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text)
  }
  const formatDate = (date: string) => {
    return DateHelper.formatDate(
      date,
      "dd/MM/yyyy HH:mm:ss"
    )
  }
  const createdAt = data?.createdAt
    ? DateHelper.formatDate(
        data.createdAt,
        "dd/MM/yyyy 'às' HH:mm"
      )
    : undefined
  const updatedAt = data?.updatedAt
    ? DateHelper.formatDate(
        data.updatedAt,
        "dd/MM/yyyy 'às' HH:mm"
      )
    : undefined
  const title =
    data?.description ??
    error?.message ??
    t("webhooks.details.notFound")
  if (isFetching) {
    return <WebhookDetailsSkeleton />
  }
  return (
    <>
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-muted-foreground">
            {t("webhooks.details.title")}
          </h2>
          {hasData && (
            <h1 className="page-title">{title}</h1>
          )}
        </CardHeader>
        <CardContent className="space-y-8">
          {!hasData && (
            <section className="rounded-md border border-destructive/40 bg-destructive/10 p-4">
              <p className="text-sm text-destructive">
                {error?.message ??
                  t("webhooks.details.loadError")}
              </p>
            </section>
          )}
          {hasData && (
            <section className="grid gap-4 lg:grid-cols-2">
              <SectionCard
                title={t("webhooks.details.information")}
                icon={<InfoIcon size={16} />}
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <InfoItem
                    label="ID"
                    value={data?.id}
                    copyable
                  />
                  <InfoItem
                    label={t(
                      "webhooks.details.description"
                    )}
                    value={data?.description}
                  />
                  <div className="sm:col-span-2">
                    <InfoItem
                      label="URL"
                      value={data?.url}
                      copyable
                    />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t("webhooks.details.status")}
                    </span>
                    <Badge
                      variant="outline"
                      className={
                        data?.active
                          ? "w-fit border-green-500 bg-green-50 text-green-700"
                          : "w-fit border-red-500 bg-red-50 text-red-700"
                      }
                    >
                      {data?.active
                        ? t("webhooks.details.active")
                        : t("webhooks.details.inactive")}
                    </Badge>
                  </div>
                  <InfoItem
                    label={t("webhooks.details.maxRetries")}
                    value={String(data?.maxRetry ?? "-")}
                  />
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t("webhooks.details.blockOnErrors")}
                    </span>
                    <Badge
                      variant="outline"
                      className={
                        data?.blockManyErrors
                          ? "w-fit border-green-500 bg-green-50 text-green-700"
                          : "w-fit border-zinc-500 bg-zinc-50 text-zinc-700"
                      }
                    >
                      {data?.blockManyErrors
                        ? t("webhooks.details.yes")
                        : t("webhooks.details.no")}
                    </Badge>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t("webhooks.details.secretKey")}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="truncate font-mono text-sm">
                        {data?.secretKey
                          ? showSecretKey
                            ? data.secretKey
                            : "••••••••••••"
                          : "-"}
                      </span>
                      {data?.secretKey && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() =>
                              setShowSecretKey(
                                !showSecretKey
                              )
                            }
                            title={
                              showSecretKey
                                ? t("webhooks.details.hide")
                                : t("webhooks.details.show")
                            }
                          >
                            {showSecretKey ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                          <ClipboardButton
                            text={data?.secretKey ?? ""}
                          />
                        </>
                      )}
                    </div>
                  </div>
                  <InfoItem
                    label={t("webhooks.details.createdAt")}
                    value={createdAt}
                  />
                  <InfoItem
                    label={t("webhooks.details.updatedAt")}
                    value={updatedAt}
                  />
                </div>
              </SectionCard>
              <SectionCard
                title={t(
                  "webhooks.details.configuredEvents"
                )}
                icon={<WebhookIcon size={16} />}
                action={
                  <Badge variant="secondary">
                    {t("webhooks.details.eventsCount", {
                      count: data?.events?.length ?? 0,
                    })}
                  </Badge>
                }
              >
                <div className="flex flex-wrap gap-2">
                  {data?.events?.map((event) => (
                    <Badge
                      key={event}
                      className="bg-primary text-primary-foreground"
                    >
                      {getEventLabel(event)}
                    </Badge>
                  ))}
                </div>
              </SectionCard>
            </section>
          )}
        </CardContent>
      </Card>
      {hasData && (
        <Card className="mt-6">
          <CardContent className="p-0">
            <Tabs
              defaultValue="resources"
              className="w-full"
            >
              <TabsList className="m-4 mb-0 w-auto">
                <TabsTrigger value="resources">
                  {t("webhooks.details.resources")}
                </TabsTrigger>
              </TabsList>
              <TabsContent
                value="resources"
                className="mt-0"
              >
                <div className="space-y-4 p-6 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">
                        {t("webhooks.details.sendHistory")}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          "webhooks.details.sendHistoryDescription"
                        )}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => refetchResources()}
                      disabled={isFetchingResources}
                    >
                      <RefreshCw
                        className={`h-4 w-4 ${isFetchingResources ? "animate-spin" : ""}`}
                      />
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Popover
                      open={resourceTypeOpen}
                      onOpenChange={setResourceTypeOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={resourceTypeOpen}
                          className="w-[280px] justify-between font-normal"
                        >
                          <span className="truncate">
                            {resourceTypeFilter
                              ? EVENT_LABELS[
                                  resourceTypeFilter as WebhookEvent
                                ]
                              : t(
                                  "webhooks.details.eventType"
                                )}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[280px] p-0">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder={t(
                              "webhooks.details.searchEvent"
                            )}
                            value={resourceTypeSearch}
                            onValueChange={
                              setResourceTypeSearch
                            }
                          />
                          <CommandList>
                            <CommandEmpty>
                              {t(
                                "webhooks.details.noEventFound"
                              )}
                            </CommandEmpty>
                            <CommandGroup>
                              {filteredEvents.map(
                                (event) => (
                                  <CommandItem
                                    key={event}
                                    value={event}
                                    onSelect={() => {
                                      setResourceTypeFilter(
                                        event
                                      )
                                      setCurrentPage(1)
                                      setResourceTypeOpen(
                                        false
                                      )
                                      setResourceTypeSearch(
                                        ""
                                      )
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        resourceTypeFilter ===
                                          event
                                          ? "opacity-100"
                                          : "opacity-0"
                                      )}
                                    />
                                    {EVENT_LABELS[event]}
                                  </CommandItem>
                                )
                              )}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <div className="w-[240px]">
                      <AppDateRangePicker
                        value={dateRange}
                        onChange={(range) => {
                          setDateRange(range)
                          setCurrentPage(1)
                        }}
                      />
                    </div>
                    <Select
                      value={statusFilter}
                      onValueChange={(v) => {
                        setStatusFilter(
                          v as WebhookResourceStatus | ""
                        )
                        setCurrentPage(1)
                      }}
                    >
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PENDING">
                          {t("webhooks.details.pending")}
                        </SelectItem>
                        <SelectItem value="SENDING">
                          {t("webhooks.details.sending")}
                        </SelectItem>
                        <SelectItem value="SENT">
                          {t("webhooks.details.sent")}
                        </SelectItem>
                        <SelectItem value="FAILED">
                          {t("webhooks.details.failed")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="relative min-w-[200px] flex-1">
                      <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="search"
                        placeholder="Resource ID"
                        value={resourceIdFilter}
                        onChange={(e) => {
                          setResourceIdFilter(
                            e.target.value
                          )
                          setCurrentPage(1)
                        }}
                        className="pl-8 [&::-webkit-search-cancel-button]:hidden"
                      />
                    </div>
                    {hasActiveFilters && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                      >
                        <X className="mr-1 h-4 w-4" />
                        {t("webhooks.details.clearFilters")}
                      </Button>
                    )}
                  </div>
                </div>
                {isFetchingResources ? (
                  <div className="relative overflow-y-auto overflow-x-hidden rounded-md border border-border">
                    <Table
                      className="relative h-10 w-full overflow-clip rounded-md border-border"
                      divClassname="max-h-[50vh] overflow-y-scroll scrollbar"
                    >
                      <TableHeader className="sticky top-0 z-20 h-10 w-full bg-background outline outline-1 outline-border">
                        <TableRow>
                          <TableHead>
                            {t("webhooks.details.date")}
                          </TableHead>
                          <TableHead>
                            {t("webhooks.details.status")}
                          </TableHead>
                          <TableHead className="hidden md:table-cell">
                            {t("webhooks.details.type")}
                          </TableHead>
                          <TableHead className="hidden lg:table-cell">
                            {t(
                              "webhooks.details.application"
                            )}
                          </TableHead>
                          <TableHead className="hidden sm:table-cell">
                            Resource ID
                          </TableHead>
                          <TableHead className="hidden sm:table-cell">
                            {t("webhooks.details.retries")}
                          </TableHead>
                          <TableHead>
                            {t("webhooks.details.actions")}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Array.from({
                          length: resourcesPageSize,
                        }).map((_, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Skeleton className="h-4 w-24" />
                            </TableCell>
                            <TableCell>
                              <Skeleton className="h-5 w-16 rounded-full" />
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              <Skeleton className="h-5 w-20 rounded-full" />
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              <Skeleton className="h-5 w-16 rounded-full" />
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <Skeleton className="h-4 w-28" />
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <Skeleton className="h-4 w-10" />
                            </TableCell>
                            <TableCell>
                              <Skeleton className="h-8 w-8 rounded" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : resources.length > 0 ? (
                  <div className="relative overflow-y-auto overflow-x-hidden rounded-md border border-border">
                    <Table
                      className="relative h-10 w-full overflow-clip rounded-md border-border"
                      divClassname="max-h-[50vh] overflow-y-scroll scrollbar"
                    >
                      <TableHeader className="sticky top-0 z-20 h-10 w-full bg-background outline outline-1 outline-border">
                        <TableRow>
                          <TableHead>
                            {t("webhooks.details.date")}
                          </TableHead>
                          <TableHead>
                            {t("webhooks.details.status")}
                          </TableHead>
                          <TableHead className="hidden md:table-cell">
                            {t("webhooks.details.type")}
                          </TableHead>
                          <TableHead className="hidden lg:table-cell">
                            {t(
                              "webhooks.details.application"
                            )}
                          </TableHead>
                          <TableHead className="hidden sm:table-cell">
                            Resource ID
                          </TableHead>
                          <TableHead className="hidden sm:table-cell">
                            {t("webhooks.details.retries")}
                          </TableHead>
                          <TableHead>
                            {t("webhooks.details.actions")}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {resources.map(
                          (resource: WebhookResource) => (
                            <TableRow
                              key={resource.id}
                              className="even:bg-zinc-50"
                            >
                              <TableCell className="whitespace-nowrap">
                                {formatDate(
                                  resource.createdAt
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  className={
                                    STATUS_COLORS[
                                      resource.status
                                    ]
                                  }
                                >
                                  {
                                    STATUS_LABELS[
                                      resource.status
                                    ]
                                  }
                                </Badge>
                              </TableCell>
                              <TableCell className="hidden md:table-cell">
                                <Badge variant="outline">
                                  {resource.resourceType}
                                </Badge>
                              </TableCell>
                              <TableCell className="hidden lg:table-cell">
                                <Badge variant="secondary">
                                  {
                                    resource.resourceApplication
                                  }
                                </Badge>
                              </TableCell>
                              <TableCell className="hidden sm:table-cell">
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      className="h-auto p-1 font-mono text-xs"
                                    >
                                      <span className="block max-w-[120px] truncate">
                                        {
                                          resource.resourceId
                                        }
                                      </span>
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent
                                    className="w-auto p-2"
                                    align="start"
                                  >
                                    <div className="flex flex-col gap-2">
                                      <div className="flex items-center gap-2">
                                        <span className="font-mono text-xs">
                                          {
                                            resource.resourceId
                                          }
                                        </span>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6"
                                          onClick={() =>
                                            copyToClipboard(
                                              resource.resourceId
                                            )
                                          }
                                        >
                                          <Copy className="h-3 w-3" />
                                        </Button>
                                      </div>
                                      {resource.resourceId?.startsWith(
                                        "ORD-"
                                      ) && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          asChild
                                          className="justify-start"
                                        >
                                          <Link
                                            to={`${FRONTEND_ROUTES.ORDERS.href}/details/${resource.resourceId}`}
                                            target="_blank"
                                          >
                                            <ExternalLink className="mr-2 h-4 w-4" />
                                            {t(
                                              "webhooks.details.viewOrder"
                                            )}
                                          </Link>
                                        </Button>
                                      )}
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              </TableCell>
                              <TableCell className="hidden sm:table-cell">
                                {resource.countRetry}/
                                {resource.maxRetry}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  {resource.status ===
                                    "FAILED" && (
                                    <Button
                                      size="sm"
                                      onClick={() =>
                                        handleResend(
                                          resource.id
                                        )
                                      }
                                      disabled={
                                        resendMutation.isPending
                                      }
                                    >
                                      <RefreshCw className="h-4 w-4 sm:mr-1" />
                                      <span className="hidden sm:inline">
                                        {t(
                                          "webhooks.details.resend"
                                        )}
                                      </span>
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() =>
                                      setSelectedResourceId(
                                        resource.id
                                      )
                                    }
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <p className="text-sm text-muted-foreground">
                      {t(
                        "webhooks.details.noResourceFound"
                      )}
                    </p>
                  </div>
                )}
                {resourcesData?.pageInfo && (
                  <div className="px-6 pb-6">
                    <CallbackPagination
                      pagination={{
                        page: currentPage,
                        pages: resourcesData.pageInfo.pages,
                        limit: resourcesPageSize,
                        count: resourcesData.pageInfo.count,
                        totalItems:
                          resourcesData.pageInfo.totalItems,
                      }}
                      onPageChange={setCurrentPage}
                      onPageSizeChange={(size) => {
                        setResourcesPageSize(size)
                        setCurrentPage(1)
                      }}
                      isLoading={isFetchingResources}
                      itemsPerPageOptions={[5, 10, 25, 50]}
                      defaultLimit={10}
                    />
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
      <Dialog
        open={!!selectedResourceId}
        onOpenChange={() => setSelectedResourceId(null)}
      >
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>
              {t("webhooks.details.resourceDetails")}
            </DialogTitle>
            <Button
              variant="outline"
              size="icon"
              onClick={() => refetchResourceDetails()}
              disabled={isFetchingResourceDetails}
            >
              <RefreshCw
                className={`h-4 w-4 ${isFetchingResourceDetails ? "animate-spin" : ""}`}
              />
            </Button>
          </DialogHeader>
          {isLoadingResource ? (
            <div className="space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : resourceDetails ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t("webhooks.details.status")}
                  </p>
                  <Badge
                    className={
                      STATUS_COLORS[resourceDetails.status]
                    }
                  >
                    {STATUS_LABELS[resourceDetails.status]}
                  </Badge>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-muted-foreground">
                    {t("webhooks.details.type")}
                  </p>
                  <Badge
                    variant="outline"
                    className="max-w-full truncate"
                  >
                    {resourceDetails.resourceType}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t("webhooks.details.retries")}
                  </p>
                  <p className="text-sm">
                    {resourceDetails.countRetry}/
                    {resourceDetails.maxRetry}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t("webhooks.details.date")}
                  </p>
                  <p className="text-sm">
                    {formatDate(resourceDetails.createdAt)}
                  </p>
                </div>
              </div>
              <Separator />
              <div>
                <p className="mb-2 text-sm font-medium text-muted-foreground">
                  Resource ID
                </p>
                <p className="break-all rounded bg-muted p-2 font-mono text-sm">
                  {resourceDetails.resourceId}
                </p>
              </div>
              <Separator />
              <Tabs
                defaultValue="events"
                className="w-full"
              >
                <TabsList className="w-full">
                  <TabsTrigger
                    value="events"
                    className="flex-1"
                  >
                    {t("webhooks.details.sendAttempts")}
                  </TabsTrigger>
                  <TabsTrigger
                    value="status"
                    className="flex-1"
                  >
                    {t("webhooks.details.statusHistory")}
                  </TabsTrigger>
                </TabsList>
                <TabsContent
                  value="status"
                  className="mt-4"
                >
                  {resourceDetails.statusEvents &&
                  resourceDetails.statusEvents.length >
                    0 ? (
                    <Timeline>
                      {resourceDetails.statusEvents.map(
                        (
                          event: WebhookStatusEvent,
                          index: number
                        ) => {
                          const isLast =
                            index ===
                            resourceDetails.statusEvents!
                              .length -
                              1
                          const statusColors: Record<
                            string,
                            string
                          > = {
                            PENDING: "bg-orange-400",
                            SENDING: "bg-blue-400",
                            SENT: "bg-green-400",
                            FAILED: "bg-red-400",
                          }
                          return (
                            <VerticalTimelineItem
                              key={index}
                            >
                              <TimelineHeader>
                                <TimelineIcon
                                  className={
                                    statusColors[
                                      event.status
                                    ] || "bg-gray-400"
                                  }
                                />
                                <TimelineTitle>
                                  <Badge
                                    variant="outline"
                                    className={
                                      STATUS_COLORS[
                                        event.status
                                      ]
                                    }
                                  >
                                    {
                                      STATUS_LABELS[
                                        event.status
                                      ]
                                    }
                                  </Badge>
                                </TimelineTitle>
                              </TimelineHeader>
                              {!isLast && (
                                <TimelineConnector />
                              )}
                              <TimelineContent>
                                <TimelineDescription>
                                  {formatDate(
                                    event.createdAt
                                  )}
                                </TimelineDescription>
                              </TimelineContent>
                            </VerticalTimelineItem>
                          )
                        }
                      )}
                    </Timeline>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t(
                        "webhooks.details.noStatusHistory"
                      )}
                    </p>
                  )}
                </TabsContent>
                <TabsContent
                  value="events"
                  className="mt-4"
                >
                  {resourceDetails.events &&
                  resourceDetails.events.length > 0 ? (
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[40px]"></TableHead>
                            <TableHead>
                              {t("webhooks.details.date")}
                            </TableHead>
                            <TableHead>ID</TableHead>
                            <TableHead>
                              {t("webhooks.details.code")}
                            </TableHead>
                            <TableHead>
                              {t("webhooks.details.status")}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {resourceDetails.events.map(
                            (
                              event: WebhookResourceEvent
                            ) => (
                              <>
                                <TableRow
                                  key={event.id}
                                  className="cursor-pointer hover:bg-muted/50"
                                  onClick={() =>
                                    setExpandedEventId(
                                      expandedEventId ===
                                        event.id
                                        ? null
                                        : event.id
                                    )
                                  }
                                >
                                  <TableCell>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                    >
                                      {expandedEventId ===
                                      event.id ? (
                                        <ChevronDown className="h-4 w-4" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    {event.createdAt
                                      ? formatDate(
                                          event.createdAt
                                        )
                                      : "-"}
                                  </TableCell>
                                  <TableCell className="font-mono text-xs">
                                    {event.id?.substring(
                                      0,
                                      8
                                    )}
                                    ...
                                  </TableCell>
                                  <TableCell>
                                    <Badge
                                      variant="outline"
                                      className={
                                        event.responseCode?.startsWith(
                                          "2"
                                        )
                                          ? "border-green-500 bg-green-50 text-green-700"
                                          : "border-red-500 bg-red-50 text-red-700"
                                      }
                                    >
                                      {event.responseCode ||
                                        "-"}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <Badge
                                      variant="outline"
                                      className={
                                        STATUS_COLORS[
                                          event.status as WebhookResourceStatus
                                        ] ||
                                        "border-zinc-500 text-zinc-700"
                                      }
                                    >
                                      {STATUS_LABELS[
                                        event.status as WebhookResourceStatus
                                      ] || event.status}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                                {expandedEventId ===
                                  event.id && (
                                  <TableRow
                                    key={`${event.id}-expanded`}
                                  >
                                    <TableCell
                                      colSpan={5}
                                      className="bg-muted/30 p-4"
                                    >
                                      <div className="space-y-3">
                                        <HttpViewer
                                          title={t(
                                            "webhooks.details.request"
                                          )}
                                          content={
                                            event.request
                                          }
                                        />
                                        <HttpViewer
                                          title={t(
                                            "webhooks.details.response"
                                          )}
                                          content={
                                            event.response
                                          }
                                          statusCode={
                                            event.responseCode
                                          }
                                        />
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t("webhooks.details.noSendAttempts")}
                    </p>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  )
}
